import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import { CheckOutlined, CloseOutlined, MoreVertOutlined, VisibilityOutlined } from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import type { FC } from "react"
import { useRef, useState } from "react"

import { type QualityApproval, QualityApprovalStatus } from "@/graphql/codegen/graphql"

interface QualityRequestRowActionsProps {
  row: CellContext<QualityApproval, unknown>["row"]
  onProductView: (qualityApproval: QualityApproval) => void
  onApprovalRequest: (qualityApproval: QualityApproval) => void
  onApprovalCancel: (qualityApproval: QualityApproval) => void
}

export const QualityRequestRowActions: FC<QualityRequestRowActionsProps> = ({
  row,
  onProductView,
  onApprovalRequest,
  onApprovalCancel,
}) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const handleMenuOpen = () => setIsMenuOpen(true)
  const handleMenuClose = () => setIsMenuOpen(false)

  const handleProductView = () => {
    handleMenuClose()
    onProductView(row.original)
  }

  const handleApprovalRequest = () => {
    handleMenuClose()
    onApprovalRequest(row.original)
  }

  const handleApprovalCancel = () => {
    handleMenuClose()
    onApprovalCancel(row.original)
  }

  return (
    <Box ref={anchorElRef}>
      <IconButton size='small' onClick={handleMenuOpen} data-testid='dashboard.quality-request.row-actions-menu-button'>
        <MoreVertOutlined />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={isMenuOpen}
        onClose={handleMenuClose}
        data-testid='dashboard.quality-request.row-actions-menu'
      >
        <MenuItem data-testid='dashboard.quality-request.view-product-menu-item' onClick={handleProductView}>
          <ListItemIcon>
            <VisibilityOutlined />
          </ListItemIcon>
          <ListItemText primary='View Product Details' />
        </MenuItem>
        {row.original.status === QualityApprovalStatus.Pending && (
          <MenuItem data-testid='dashboard.quality-request.cancel-approval-menu-item' onClick={handleApprovalCancel}>
            <ListItemIcon>
              <CloseOutlined />
            </ListItemIcon>
            <ListItemText primary='Cancel Out of Spec Approval Request' />
          </MenuItem>
        )}
        {row.original.status === QualityApprovalStatus.Denied && (
          <MenuItem data-testid='dashboard.quality-request.request-approval-menu-item' onClick={handleApprovalRequest}>
            <ListItemIcon>
              <CheckOutlined />
            </ListItemIcon>
            <ListItemText primary='Request Out of Spec Approval' />
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}
